import { useEffect, useMemo, useState } from 'react'
import { Alert } from '../../components/atoms/Alert'
import { Button } from '../../components/atoms/Button'
import { Checkbox } from '../../components/atoms/Checkbox'
import { Input } from '../../components/atoms/Input'
import { Modal } from '../../components/atoms/Modal'
import { Textarea } from '../../components/atoms/Textarea'
import { Field } from '../../components/molecules/Field'
import { Th, Td } from '../../components/molecules/Table'
import { ACTIONFLAGS, ACTIONLABELS, type ActionFlag } from '../../../constants/action.constants'
import { GROUPDESCMAX, GROUPNAMEMAX } from '../../../constants/group.constants'
import type {
  CreateGroupInput,
  GroupDto,
  GroupScreenGrant,
  ScreenActionCatalogueRow,
  UpdateGroupInput
} from '../../../viewmodels/useGroups'

interface Props {
  readonly open: boolean
  readonly mode: 'create' | 'edit'
  readonly initial: GroupDto | null
  readonly screenActions: readonly ScreenActionCatalogueRow[]
  readonly onClose: () => void
  readonly onSubmit: (
    input: CreateGroupInput | UpdateGroupInput
  ) => Promise<{ ok: true } | { ok: false; field?: string | null; message: string }>
}

type FlagState = Record<ActionFlag, boolean>

interface FormState {
  name: string
  description: string
  is_active: boolean
  /** screen_id → which actions are granted. */
  grants: Map<number, FlagState>
}

const EMPTY_FLAGS: FlagState = {
  is_view: false,
  is_add: false,
  is_edit: false,
  is_delete: false,
  is_export: false
}

const EMPTY: FormState = {
  name: '',
  description: '',
  is_active: true,
  grants: new Map()
}

function flagOf(b: boolean): 0 | 1 {
  return b ? 1 : 0
}

function hasAny(f: FlagState): boolean {
  return ACTIONFLAGS.some((flag) => f[flag])
}

function clientValidate(state: FormState): { field: keyof FormState | null; message: string } | null {
  const name = state.name.trim()
  if (name.length < 1 || name.length > GROUPNAMEMAX) {
    return { field: 'name', message: `name must be 1..${GROUPNAMEMAX} chars` }
  }
  if (state.description.length > GROUPDESCMAX) {
    return { field: 'description', message: `description must be ≤${GROUPDESCMAX} chars` }
  }
  return null
}

export function GroupFormModal({
  open,
  mode,
  initial,
  screenActions,
  onClose,
  onSubmit
}: Props): JSX.Element {
  const [state, setState] = useState<FormState>(EMPTY)
  const [submitting, setSubmitting] = useState(false)
  const [fieldError, setFieldError] = useState<{ field: keyof FormState; message: string } | null>(null)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    const grants = new Map<number, FlagState>()
    if (initial !== null) {
      for (const g of initial.grants) {
        grants.set(g.screen_id, {
          is_view: g.is_view === 1,
          is_add: g.is_add === 1,
          is_edit: g.is_edit === 1,
          is_delete: g.is_delete === 1,
          is_export: g.is_export === 1
        })
      }
    }
    setState(
      initial === null
        ? { ...EMPTY, grants }
        : {
            name: initial.name,
            description: initial.description ?? '',
            is_active: initial.is_active === 1,
            grants
          }
    )
    setFieldError(null)
    setFormError(null)
    setSubmitting(false)
  }, [open, initial])

  const grantedScreenCount = useMemo(
    () => Array.from(state.grants.values()).filter(hasAny).length,
    [state.grants]
  )

  const toggle = (screenId: number, flag: ActionFlag): void => {
    setState((s) => {
      const next = new Map(s.grants)
      const cur = next.get(screenId) ?? EMPTY_FLAGS
      next.set(screenId, { ...cur, [flag]: !cur[flag] })
      return { ...s, grants: next }
    })
  }

  const handleSubmit = async (): Promise<void> => {
    const clientErr = clientValidate(state)
    if (clientErr !== null) {
      if (clientErr.field !== null) setFieldError({ field: clientErr.field, message: clientErr.message })
      else setFormError(clientErr.message)
      return
    }
    setFieldError(null)
    setFormError(null)
    setSubmitting(true)

    const grants: GroupScreenGrant[] = []
    for (const row of screenActions) {
      const f = state.grants.get(row.screen_id)
      if (f === undefined || !hasAny(f)) continue
      grants.push({
        screen_id: row.screen_id,
        is_view: flagOf(f.is_view),
        is_add: flagOf(f.is_add),
        is_edit: flagOf(f.is_edit),
        is_delete: flagOf(f.is_delete),
        is_export: flagOf(f.is_export)
      })
    }

    const payload: CreateGroupInput | UpdateGroupInput = {
      name: state.name.trim(),
      description: state.description === '' ? null : state.description,
      is_active: state.is_active,
      grants
    }
    const result = await onSubmit(payload)
    setSubmitting(false)
    if (result.ok) {
      onClose()
      return
    }
    const fld = result.field
    if (fld !== null && fld !== undefined && fld in EMPTY) {
      setFieldError({ field: fld as keyof FormState, message: result.message })
    } else {
      setFormError(result.message)
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={mode === 'create' ? 'Add group' : `Edit ${initial?.name ?? ''}`}
      maxWidth="lg"
      footer={
        <>
          <Button variant="secondary" onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button onClick={() => void handleSubmit()} disabled={submitting}>
            {submitting ? 'Saving…' : mode === 'create' ? 'Add' : 'Save'}
          </Button>
        </>
      }
    >
      <div className="space-y-4">
        {formError !== null ? <Alert tone="error">{formError}</Alert> : null}

        <Field label="Name" error={fieldError?.field === 'name' ? fieldError.message : null}>
          <Input
            value={state.name}
            hasError={fieldError?.field === 'name'}
            onChange={(e) => setState((s) => ({ ...s, name: e.target.value }))}
            placeholder="e.g. Customer Service"
            maxLength={GROUPNAMEMAX}
          />
        </Field>

        <Field label="Description" error={fieldError?.field === 'description' ? fieldError.message : null}>
          <Textarea
            value={state.description}
            rows={2}
            hasError={fieldError?.field === 'description'}
            onChange={(e) => setState((s) => ({ ...s, description: e.target.value }))}
            placeholder="Optional"
            maxLength={GROUPDESCMAX}
          />
        </Field>

        <Checkbox
          label="Active"
          checked={state.is_active}
          onChange={(e) => setState((s) => ({ ...s, is_active: e.target.checked }))}
        />

        <PermissionGrid
          rows={screenActions}
          grants={state.grants}
          grantedScreenCount={grantedScreenCount}
          onToggle={toggle}
        />
      </div>
    </Modal>
  )
}

function PermissionGrid({
  rows,
  grants,
  grantedScreenCount,
  onToggle
}: {
  readonly rows: readonly ScreenActionCatalogueRow[]
  readonly grants: ReadonlyMap<number, FlagState>
  readonly grantedScreenCount: number
  readonly onToggle: (screenId: number, flag: ActionFlag) => void
}): JSX.Element {
  const [query, setQuery] = useState('')

  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.screen_name.localeCompare(b.screen_name) || a.screen_id - b.screen_id),
    [rows]
  )
  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase()
    if (q === '') return sorted
    return sorted.filter((r) => r.screen_name.toLowerCase().includes(q))
  }, [sorted, query])

  return (
    <div className="space-y-2 border-t border-border pt-4 mt-2">
      <div className="flex items-baseline justify-between gap-3">
        <span className="block text-body-sm font-body text-text-primary">Permissions</span>
        <span className="text-body-xs text-text-secondary">
          {grantedScreenCount} screen{grantedScreenCount === 1 ? '' : 's'} granted
        </span>
      </div>
      <p className="text-body-xs text-text-secondary">
        Tick the actions this group can perform per screen. Cells a screen
        doesn’t support are disabled.
      </p>

      <Input
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder="Search by screen name…"
      />

      <div className="rounded-md border border-border overflow-auto max-h-80">
        <table className="w-full font-body text-body-sm">
          <thead className="bg-parchment/50 text-text-secondary sticky top-0">
            <tr>
              <Th>Screen</Th>
              {ACTIONFLAGS.map((flag) => (
                <Th key={flag} className="text-center">
                  {ACTIONLABELS[flag]}
                </Th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr>
                <td
                  colSpan={ACTIONFLAGS.length + 1}
                  className="px-3 py-4 font-body text-body-sm text-text-secondary text-center"
                >
                  No screens match “{query}”.
                </td>
              </tr>
            ) : (
              filtered.map((row) => {
                const f = grants.get(row.screen_id) ?? EMPTY_FLAGS
                return (
                  <tr key={row.screen_id} className="border-t border-border">
                    <Td>
                      <span className="font-body text-body-sm text-charcoal">{row.screen_name}</span>
                    </Td>
                    {ACTIONFLAGS.map((flag) => (
                      <Td key={flag} className="text-center">
                        {row[flag] === 1 ? (
                          <span className="inline-flex justify-center">
                            <Checkbox
                              checked={f[flag]}
                              onChange={() => onToggle(row.screen_id, flag)}
                              aria-label={`${ACTIONLABELS[flag]} on ${row.screen_name}`}
                            />
                          </span>
                        ) : (
                          <span className="text-border" aria-hidden>
                            –
                          </span>
                        )}
                      </Td>
                    ))}
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}
